import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { matchPath, useLocation } from 'react-router';
import styled from 'styled-components';

import { Link, catchAll } from '@sorare/routing';
import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandL } from '@sorare/core/src/atoms/typography';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import { EdgeRectangleTrapezoid } from '@sorare/core/src/atoms/ui/EdgeRectangleTrapezoid';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import { CategoryTile } from '#src/components/missions/CategoryTile/index.tsx';
import { categories } from '#src/components/missions/MissionsCategoryTabsLayout/index.tsx';

import type {
  MissionsCategorySelectorQuery,
  MissionsCategorySelectorQueryVariables,
} from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 3 })`
  padding: var(--double-unit);
`;
const RelativeWrapper = styled.div`
  position: relative;
`;
const AbsoluteStarBall = styled(StarBall)`
  position: absolute;
  right: calc(-1 * var(--double-unit));
  top: 0;
`;

const MISSIONS_CATEGORY_SELECTOR_QUERY = gql`
  query MissionsCategorySelectorQuery($sport: Sport!) {
    currentUser {
      slug
      taskGroups {
        slug
        myTasks(sport: $sport) {
          id
          aasmState
        }
        totalRewardConfigs(sport: $sport) {
          id
          ...AnyRewardConfig_anyRewardConfigInterface
        }
      }
    }
  }
  ${AnyRewardConfig.fragments.anyRewardConfigInterface}
` as TypedDocumentNode<
  MissionsCategorySelectorQuery,
  MissionsCategorySelectorQueryVariables
>;

type Props = { sport: Sport };
export const MissionsCategorySelector = ({ sport }: Props) => {
  const { pathname } = useLocation();

  const { data } = useQuery(MISSIONS_CATEGORY_SELECTOR_QUERY, {
    variables: { sport },
  });

  const { taskGroups } = data?.currentUser || {};

  return (
    <Root>
      {categories(sport).map(({ slug, title, link }) => {
        const taskGroup = taskGroups?.find(tg => slug === tg.slug);
        if (!taskGroup) {
          return null;
        }

        const selected = matchPath(catchAll(link), pathname);
        const unclaimedTasks = taskGroup.myTasks.filter(
          ({ aasmState }) => aasmState === ManagerProgressionTaskState.COMPLETED
        );

        return (
          <Link key={link} to={link}>
            <CategoryTile
              disabled={!selected}
              topRight={
                <Badge badgeContent={unclaimedTasks.length} right={1} />
              }
              footer={
                <EdgeRectangleTrapezoid
                  padding="var(--half-unit) var(--triple-unit)"
                  border={`linear-gradient(to bottom, ${selected ? 'rgba(61, 220, 132, 1)' : 'transparent'} 0%, #111F1C 70%)`}
                >
                  <Horizontal>
                    {taskGroup?.totalRewardConfigs.map(rewardConfig => (
                      <AnyRewardConfig
                        key={rewardConfig.id}
                        hideText
                        anyRewardConfig={rewardConfig}
                        variant={Variant.TEXT}
                        rarity="mix"
                      />
                    ))}
                  </Horizontal>
                </EdgeRectangleTrapezoid>
              }
            >
              <RelativeWrapper>
                <BrandL>{title}</BrandL>
                <AbsoluteStarBall width={16} height={16} />
              </RelativeWrapper>
            </CategoryTile>
          </Link>
        );
      })}
    </Root>
  );
};
